import { useEffect, useState } from "react";
import { 
    Avatar, Box, Button, Container, Grid, MenuItem, Card, Tab, Tabs, 
    TextField, Typography, useTheme, Stack, Divider, IconButton, Tooltip, 
    Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, 
    ListSubheader, Chip
} from "@mui/material";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faBarsStaggered, faMoneyBillWave, faReceipt, faTruckFast, faUser, 
    faCircleDot, faMapMarkerAlt, faLocationDot, faPhone, faEnvelope,
    faPlus
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import PropTypes from 'prop-types';
import styled from "styled-components";
import { VerticalTimeline, VerticalTimelineElement }  from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';

// Componentes QUD
import CustomBreadcrumbs from "../../../components/breadcrumbs";
import OrderDetailsDataTable from "./detailsDataTable";
import OrderDetailsPaymentsDataTable from "./detailsPaymentsDataTable";
import MapContainer from '../../../components/map';
import QUDProgress from "../../../components/progress";
import { getOrderDetails, getPaymentDestinations, registerManualPayment, updateOrderStatus } from "../../../controllers/company/OrdersController";
import { imagesURL } from "../../../apiRoutes/apiBase";
import { LoadingButton } from "@mui/lab";

// --- ESTILOS DEL TIMELINE ---
const CustomTimeline = styled(Box)`
    .vertical-timeline::before { background: #e0e0e0; }
    .vertical-timeline-element-icon {
        box-shadow: 0 0 0 4px #e3f2fd, inset 0 2px 0 rgba(0,0,0,.08), 0 3px 0 4px rgba(0,0,0,.05) !important;
    }
    .vertical-timeline-element-content {
        box-shadow: 0 4px 20px rgba(0,0,0,0.03) !important;
        border: 1px solid #e0e0e0;
        border-radius: 12px;
        padding: 1rem;
    }
`;

// --- TAB PANEL ---
function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
      <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
        {value === index && <Box sx={{ pt: { xs: 2, sm: 3 } }}>{children}</Box>}
      </div>
    );
}
CustomTabPanel.propTypes = { children: PropTypes.node, index: PropTypes.number.isRequired, value: PropTypes.number.isRequired };
function a11yProps(index) { return { id: `simple-tab-${index}`, 'aria-controls': `simple-tabpanel-${index}` }; }

export default function OrderDetails() {
    const theme = useTheme();
    const { code } = useParams();
    const [initialCenter, setInitialCenter] = useState({ lat: 10.189640, lng: -64.688094 });
    const [value, setValue] = useState(0);

    const [isLoading, setIsLoading] = useState(false);
    const [order, setOrder] = useState({});
    const [details, setDetails] = useState([]);
    const [payments, setPayments] = useState([]);
    const [customer, setCustomer] = useState({});
    const [tracking, setTracking] = useState([]);

    // --- ESTADOS PARA EL MODAL DE PAGO MANUAL ---
    const [openPaymentModal, setOpenPaymentModal] = useState(false);
    const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
    const [manualPayment, setManualPayment] = useState({
        monto: '',
        id_cuenta_destino: '', 
        referencia: '',
        fecha: new Date().toISOString().split('T')[0]
    });

    const [cuentasBancarias, setCuentasBancarias] = useState({ comercio: [], qud: [] });

    useEffect(() => {
        window.scrollTo(0, 0);
        handleGetOrderDetails();
        fetchPaymentDestinations();
    }, []);

    const handleChange = (event, newValue) => setValue(newValue);

    const handleGetOrderDetails = async () => {
        setIsLoading(true);
        let response = await getOrderDetails(code);
        
        if (response.success === true) {
            // El backend ahora devuelve la data directa y limpia
            const data = response.data;
            setOrder(data || {});
            setDetails(data.items || []); // Cambiado de details a items
            setPayments(data.payments || []);
            setCustomer(data.customer || {});
            
            // Si agregas tracking en tu controlador, aquí se leerá
            setTracking(data.tracking || []); 
            
            if(data.origin_latitude) {
                setInitialCenter({ lat: data.origin_latitude, lng: data.origin_longitude });
            }
        }
        setIsLoading(false);
    }

    const fetchPaymentDestinations = async () => {
        const response = await getPaymentDestinations();
        if (response.success) {
            setCuentasBancarias({
                comercio: response.data.comercio || [],
                qud: response.data.qud || []
            });
        }
    }

    const handleUpdateOrderStatus = async (estatus) => {
        setIsLoading(true);
        // Usamos order_id en lugar de id_pedido
        let response = await updateOrderStatus(order.order_id, estatus);
        if (response.success === true) handleGetOrderDetails();
        setIsLoading(false);
    }

    const handleRegisterPayment = async () => {
        if (!manualPayment.monto || manualPayment.monto <= 0) {
            alert("El monto debe ser mayor a 0");
            return;
        }
        if (!manualPayment.id_cuenta_destino) {
            alert("Debes seleccionar una cuenta destino");
            return;
        }

        setIsSubmittingPayment(true);
        
        let response = await registerManualPayment(order.order_id, manualPayment);
        if (response.success) {
            setOpenPaymentModal(false);
            handleGetOrderDetails(); 
            setManualPayment({
                monto: '', id_cuenta_destino: '', referencia: '', 
                fecha: new Date().toISOString().split('T')[0]
            });
        } else {
            alert(response.message || "Error al registrar el pago");
        }

        setIsSubmittingPayment(false);
    }

    const cardStyle = { elevation: 0, sx: { border: '1px solid #e0e0e0', borderRadius: 3, p: { xs: 2, sm: 2.5 }, bgcolor: '#ffffff' } };

    return (
        <Container maxWidth='xl' sx={{ py: { xs: 2, sm: 4 }, mb: 8 }}>
            <CustomBreadcrumbs returnTo='/orders' crumbs={[{ name: 'Pedidos', link: '/orders' }, { name: `Pedido #${code}`, link: '#' }]} />
            
            {/* --- CABECERA PRINCIPAL --- */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mt: 3, mb: 3, gap: 2 }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="h4" fontWeight={800} sx={{ color: '#222', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                            Pedido #{order.code || code}
                        </Typography>
                        {/* Etiqueta Financiera (Controlada 100% por Backend) */}
                        {order.financial_status_label && (
                            <Chip 
                                label={order.financial_status_label} 
                                color={order.financial_status_color} 
                                variant="outlined"
                                sx={{ fontWeight: 'bold', borderWidth: 2 }} 
                            />
                        )}
                    </Box>
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                        Realizado el: {order.created_at ? new Date(order.created_at).toLocaleString() : ''}
                    </Typography>
                </Box>
                
                {/* Selector de Estado Operativo (0 al 6) */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' }, width: { xs: '100%', md: 'auto' } }}>
                    {order.status_code === 1 && (
                        <Button variant="outlined" color="error" fullWidth sx={{ borderRadius: 2, fontWeight: 'bold', textTransform: 'none' }} onClick={() => handleUpdateOrderStatus(0)}>
                            Rechazar
                        </Button>
                    )}
                    
                    <TextField
                        select size="small" label='Estado del pedido'
                        value={order.status_code !== undefined ? order.status_code : ''}
                        onChange={(e) => handleUpdateOrderStatus(e.target.value)}
                        disabled={order.status_code === 0 || order.status_code === 6}
                        fullWidth
                        sx={{ minWidth: { sm: 200 }, bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    >
                        <MenuItem value={0}>Rechazado</MenuItem>
                        <MenuItem value={1}>Realizado</MenuItem>
                        <MenuItem value={2}>Aceptado</MenuItem>
                        <MenuItem value={3}>Por Enviar</MenuItem>
                        <MenuItem value={4}>Enviado</MenuItem>
                        <MenuItem value={5}>Entregado</MenuItem>
                        <MenuItem value={6}>Finalizado</MenuItem>
                    </TextField>
                </Box>
            </Box>

            {/* --- TARJETAS DE RESUMEN --- */}
            <Grid container spacing={2} mb={4}>
                <Grid item xs={12} sm={4}>
                    <Card {...cardStyle}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#f0f4f8', color: '#1976d2', borderRadius: 2 }}>
                                <FontAwesomeIcon icon={faUser} size="sm" />
                            </Avatar>
                            <Box flexGrow={1} overflow="hidden">
                                <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Cliente</Typography>
                                <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>{customer.name || 'Cargando...'}</Typography>
                            </Box>
                        </Stack>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card {...cardStyle}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: 2 }}>
                                <FontAwesomeIcon icon={faMoneyBillWave} size="sm" />
                            </Avatar>
                            <Box flexGrow={1} overflow="hidden">
                                <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Monto Total</Typography>
                                <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>${parseFloat(order.amount || 0).toFixed(2)} {order.currency || 'USD'}</Typography>
                            </Box>
                        </Stack>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card {...cardStyle}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#fff3e0', color: '#ed6c02', borderRadius: 2 }}>
                                <FontAwesomeIcon icon={faTruckFast} size="sm" />
                            </Avatar>
                            <Box flexGrow={1} overflow="hidden">
                                <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Tipo de Entrega</Typography>
                                {/* Asume que agregarás delivery_type en tu controlador backend, o ajusta la variable */}
                                <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>{order.delivery_type || 'No definido'}</Typography>
                            </Box>
                        </Stack>
                    </Card>
                </Grid>
            </Grid>

            {/* --- PESTAÑAS --- */}
            <Box sx={{ borderBottom: 1, borderColor: '#e0e0e0' }}>
                <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile indicatorColor="primary">
                    <Tab icon={<FontAwesomeIcon icon={faReceipt} />} iconPosition="start" label={<Typography fontWeight="bold" variant="body2" textTransform="none">Artículos</Typography>} {...a11yProps(0)} />
                    <Tab icon={<FontAwesomeIcon icon={faTruckFast} />} iconPosition="start" label={<Typography fontWeight="bold" variant="body2" textTransform="none">Entrega</Typography>} {...a11yProps(1)} />
                    <Tab icon={<FontAwesomeIcon icon={faUser} />} iconPosition="start" label={<Typography fontWeight="bold" variant="body2" textTransform="none">Cliente</Typography>} {...a11yProps(2)} />
                    <Tab icon={<FontAwesomeIcon icon={faMoneyBillWave} />} iconPosition="start" label={<Typography fontWeight="bold" variant="body2" textTransform="none">Pagos</Typography>} {...a11yProps(3)} />
                    <Tab icon={<FontAwesomeIcon icon={faBarsStaggered} />} iconPosition="start" label={<Typography fontWeight="bold" variant="body2" textTransform="none">Seguimiento</Typography>} {...a11yProps(4)} />
                </Tabs>
            </Box>

            {/* --- 0: ARTÍCULOS --- */}
            <CustomTabPanel value={value} index={0}>
                {details.length !== 0 ? (
                    <OrderDetailsDataTable details={details} total={order.amount} />
                ) : (
                    <Typography color="text.secondary" textAlign="center" py={5}>No se encontraron artículos en este pedido.</Typography>
                )}
            </CustomTabPanel>

            {/* --- 1: ENTREGA --- */}
            <CustomTabPanel value={value} index={1}>
                <Grid container spacing={3}>
                    <Grid item xs={12} lg={5}>
                        <Card {...cardStyle}>
                            <Typography variant="h6" fontWeight="bold" mb={3}>Ruta de Entrega</Typography>
                            <Stack direction="row" spacing={2} mb={3}>
                                <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2', width: 36, height: 36 }}><FontAwesomeIcon icon={faLocationDot} size="sm" /></Avatar>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="bold" textTransform="uppercase">Origen (Comercio)</Typography>
                                    <Typography variant="body2" fontWeight="500">{order.origin_address || 'No especificada'}</Typography>
                                </Box>
                            </Stack>
                            <Divider sx={{ ml: 2.2, my: 1, borderLeft: '2px dashed #e0e0e0', width: 0, height: 30 }} />
                            <Stack direction="row" spacing={2} mt={1}>
                                <Avatar sx={{ bgcolor: '#ffebee', color: '#c62828', width: 36, height: 36 }}><FontAwesomeIcon icon={faMapMarkerAlt} size="sm" /></Avatar>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" fontWeight="bold" textTransform="uppercase">Destino (Cliente)</Typography>
                                    <Typography variant="body2" fontWeight="500">{order.destination_address || 'No especificada'}</Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>
                    <Grid item xs={12} lg={7}>
                        <Card sx={{ border: '1px solid #e0e0e0', borderRadius: 3, overflow: 'hidden', height: { xs: 250, sm: 350 } }}>
                            <MapContainer center={initialCenter} />
                        </Card>
                    </Grid>
                </Grid>
            </CustomTabPanel>

            {/* --- 2: CLIENTE --- */}
            <CustomTabPanel value={value} index={2}>
                <Card {...cardStyle} sx={{ ...cardStyle.sx, maxWidth: 500, mx: { xs: 'auto', md: 0 } }}>
                    <Grid container spacing={3} alignItems="center" textAlign={{ xs: 'center', sm: 'left' }}>
                        <Grid item xs={12} sm={4} display="flex" justifyContent="center">
                            <Avatar src={customer.image ? imagesURL + 'users/' + customer.image : ''} sx={{ width: 100, height: 100, border: '4px solid #f8f9fa', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }} />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <Typography variant="h5" fontWeight="bold" gutterBottom>{customer.name || 'Usuario'}</Typography>
                            <Typography variant="body2" color="text.secondary" mb={0.5}><strong>Documento:</strong> {customer.document || 'N/A'}</Typography>
                            <Typography variant="body2" color="text.secondary" mb={0.5}><strong>Teléfono:</strong> {customer.phone || 'N/A'}</Typography>
                            <Typography variant="body2" color="text.secondary" mb={2}><strong>Correo:</strong> {customer.email || 'N/A'}</Typography>
                            
                            <Stack direction="row" spacing={1.5} justifyContent={{ xs: 'center', sm: 'flex-start' }} mt={2}>
                                <Tooltip title="WhatsApp">
                                    <IconButton href={`https://wa.me/${customer.phone}`} target="_blank" sx={{ bgcolor: '#e8f5e9', color: '#25D366', '&:hover': { bgcolor: '#c8e6c9' }, width: 45, height: 45 }}>
                                        <FontAwesomeIcon icon={faWhatsapp} size="lg" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Llamar">
                                    <IconButton href={`tel:${customer.phone}`} sx={{ bgcolor: '#e3f2fd', color: '#1976d2', '&:hover': { bgcolor: '#bbdefb' }, width: 45, height: 45 }}>
                                        <FontAwesomeIcon icon={faPhone} size="sm" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Enviar Correo">
                                    <IconButton href={`mailto:${customer.email}`} sx={{ bgcolor: '#f5f5f5', color: '#616161', '&:hover': { bgcolor: '#e0e0e0' }, width: 45, height: 45 }}>
                                        <FontAwesomeIcon icon={faEnvelope} size="sm" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Grid>
                    </Grid>
                </Card>
            </CustomTabPanel>

            {/* --- 3: PAGOS --- */}
            <CustomTabPanel value={value} index={3}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold">Historial de Pagos</Typography>
                    <Button 
                        variant="contained" color="primary"
                        startIcon={<FontAwesomeIcon icon={faPlus} />} 
                        onClick={() => setOpenPaymentModal(true)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                    >
                        Registrar Pago Manual
                    </Button>
                </Box>
                {payments.length !== 0 ? (
                    <OrderDetailsPaymentsDataTable payments={payments} />
                ) : (
                    <Typography color="text.secondary" textAlign="center" py={5}>No hay pagos registrados para este pedido.</Typography>
                )}
            </CustomTabPanel>

            {/* --- 4: SEGUIMIENTO --- */}
            <CustomTabPanel value={value} index={4}>
                <CustomTimeline>
                    <VerticalTimeline layout="1-column-left" lineColor="#e0e0e0">
                        {tracking.map((item, index) => (
                            <VerticalTimelineElement
                                key={item.id || index} // Usamos el ID real si existe, sino el index
                                icon={<FontAwesomeIcon icon={faCircleDot} />}
                                iconStyle={{ background: '#fff', color: '#1976d2' }}
                                date={<Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ ml: { sm: 1 } }}>{new Date(item.created_at).toLocaleString()}</Typography>}
                            >
                                {/* Nombre del Estatus (ej. Completado) */}
                                <Typography variant='subtitle1' fontWeight={700} color="#222">
                                    {item.status_name}
                                </Typography>
                                
                                {/* Nombre de quien hizo la operación */}
                                <Typography variant='body2' fontWeight={600} color="primary.main" mb={1}>
                                    {item.operator_name}
                                </Typography>
                                
                                {/* El comentario o detalle de la acción */}
                                <Typography variant='body2' color="text.secondary">
                                    {item.observation}
                                </Typography>
                            </VerticalTimelineElement>
                        ))}
                    </VerticalTimeline>
                </CustomTimeline>
            </CustomTabPanel>

            <QUDProgress open={isLoading} />

            {/* --- MODAL DE PAGO MANUAL --- */}
            <Dialog open={openPaymentModal} onClose={() => setOpenPaymentModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">Registrar Pago Manual</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Concilia un pago recibido por fuera de la pasarela automática.
                    </Typography>
                </DialogTitle>
                
                <DialogContent sx={{ pt: 3 }}>
                    <Grid container spacing={2.5}>
                        
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>Monto Recibido</Typography>
                            <TextField
                                fullWidth size="small" type="number" placeholder="0.00"
                                value={manualPayment.monto}
                                onChange={(e) => setManualPayment({...manualPayment, monto: e.target.value})}
                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>Fecha de Pago</Typography>
                            <TextField
                                fullWidth size="small" type="date"
                                value={manualPayment.fecha}
                                onChange={(e) => setManualPayment({...manualPayment, fecha: e.target.value})}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>¿A dónde ingresó el dinero?</Typography>
                            <TextField
                                select fullWidth size="small"
                                value={manualPayment.id_cuenta_destino}
                                onChange={(e) => setManualPayment({...manualPayment, id_cuenta_destino: e.target.value})}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            >
                                {cuentasBancarias?.comercio?.length > 0 && [
                                    <ListSubheader key="hdr-com" sx={{ bgcolor: '#f8f9fa', lineHeight: '30px', fontWeight: 'bold', color: 'primary.main' }}>
                                        Mis Cuentas (Comercio)
                                    </ListSubheader>,
                                    ...cuentasBancarias.comercio.map((cuenta) => (
                                        <MenuItem key={`com-${cuenta.id}`} value={cuenta.id}>
                                            {cuenta.banco} - {cuenta.tipo} ({cuenta.numero})
                                        </MenuItem>
                                    ))
                                ]}

                                {cuentasBancarias?.qud?.length > 0 && [
                                    <ListSubheader key="hdr-qud" sx={{ bgcolor: '#f8f9fa', lineHeight: '30px', fontWeight: 'bold', color: '#111' }}>
                                        Cuentas de QUD
                                    </ListSubheader>,
                                    ...cuentasBancarias.qud.map((cuenta) => (
                                        <MenuItem key={`qud-${cuenta.id}`} value={cuenta.id}>
                                            {cuenta.banco} - {cuenta.tipo} ({cuenta.numero})
                                        </MenuItem>
                                    ))
                                ]}
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>Referencia (Opcional)</Typography>
                            <TextField
                                fullWidth size="small" placeholder="Ej: 847593"
                                value={manualPayment.referencia}
                                onChange={(e) => setManualPayment({...manualPayment, referencia: e.target.value})}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                        </Grid>

                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 3, borderTop: '1px solid #eee' }}>
                    <Button onClick={() => setOpenPaymentModal(false)} sx={{ color: 'text.secondary', fontWeight: 'bold', textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <LoadingButton 
                        loading={isSubmittingPayment} variant="contained" onClick={handleRegisterPayment}
                        disableElevation sx={{ borderRadius: 2, fontWeight: 'bold', textTransform: 'none' }}
                    >
                        Guardar Pago
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Container>
    );
}